import React, { useState, useMemo, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import PaginationComponentOne from "../../components/Pagination/Pagination-O";
import SearchComponent from "../../components/Search";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchStockStatusPerStore } from "../../redux/Inventory/inventoryActions";
import EmptyState from "../../components/EmptyState";
import StockSummaryDrawer from "./StockSummaryDrawer";

const InventoryTab = ({ activeTab, stockAdded }) => {
  const [searchValue, setSearchValue] = useState("");
  const [tableFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showStockSummaryDrawer, setShowStockSummaryDrawer] = useState(false);
  const [drawerLocationId, setDrawerLocationId] = useState("");
  const [searchParams] = useSearchParams();
  const variant = searchParams.get("variant");
  const dispatch = useDispatch();
  const { stockStatusByStore, loading } = useSelector(
    (state) => state.inventory
  );

  const handlePageChange = (paginationData) => {
    // Carbon's Pagination component passes an object with page and pageSize
    if (typeof paginationData === "object") {
      if (paginationData.page !== undefined) {
        setPage(paginationData.page);
      }
      if (
        paginationData.pageSize !== undefined &&
        paginationData.pageSize !== pageSize
      ) {
        setPageSize(paginationData.pageSize);
        setPage(1); // Reset to first page when page size changes
      }
    } else {
      setPage(paginationData);
    }
  };

  // Frontend search filtering logic
  const filteredStockData = useMemo(() => {
    if (!stockStatusByStore) return [];

    if (!searchValue.trim()) {
      return stockStatusByStore;
    }

    const searchTerm = searchValue.toLowerCase();

    return stockStatusByStore.filter((product) => {
      const stockIdMatch = (product.stock_id || "")
        .toLowerCase()
        .includes(searchTerm);
      const storeNameMatch = (product.location_name || "")
        .toLowerCase()
        .includes(searchTerm);

      return stockIdMatch || storeNameMatch;
    });
  }, [stockStatusByStore, searchValue]);

  // Paginated filtered data
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredStockData.slice(startIndex, endIndex);
  }, [filteredStockData, page, pageSize]);

  // Total count for pagination (filtered results)
  const totalFilteredItems = filteredStockData.length;

  const handleSearch = useMemo(
    () =>
      debounce((val) => {
        setPage(1); // Reset to first page on search
      }, 500),
    []
  );

  const handleChange = useCallback(
    (e) => {
      let val = e.target.value;
      setSearchValue(val);
      handleSearch(val);
    },
    [handleSearch]
  );

  useEffect(() => {
    // Only dispatch if variant is available
    if (variant) {
      dispatch(
        fetchStockStatusPerStore({
          variantId: variant,
          status: tableFilter,
          page: page,
          page_size: pageSize,
        })
      );
    }

    if (stockAdded) {
      dispatch(
        fetchStockStatusPerStore({
          variantId: variant,
          status: tableFilter,
          page: page,
          page_size: pageSize,
        })
      );
    }
  }, [dispatch, variant, tableFilter, page, pageSize, stockAdded]);

  return (
    <div className="mt-5 bg-white rounded-[8px] border border-[#E1E6E1] py-4 px-4 col-span-3">
      <div>
        <p className="text-[#3E473F] font-semibold text-lg">Inventory</p>
      </div>

      {/* search section */}
      <div className="mt-4">
        <SearchComponent
          placeholder="Search Store Name, Stock ID (STK ID)"
          searchValue={searchValue}
          handleChange={handleChange}
        />
      </div>

      <div className="overflow-x-auto relative mb-3 py-4">
        {loading ? (
          <div className="animate-pulse w-full mb-4">
            <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg  w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg  w-full"></div>
          </div>
        ) : !stockStatusByStore || stockStatusByStore.length === 0 ? (
          <div className="">
            <EmptyState text="No inventory data available yet!" />
          </div>
        ) : filteredStockData.length === 0 ? (
          <div className="">
            <EmptyState text="No inventory items match your search!" />
          </div>
        ) : (
          <>
            <table className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1]">
              <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
                <tr className="text-sm ">
                  <th className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tl-lg rounded-bl-lg  ">
                    STK ID
                  </th>

                  <th className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tl-lg rounded-bl-lg  ">
                    Store Name
                  </th>

                  <th
                    scope="col"
                    className="px-4 py-4 font-semibold text-[#5F6D60]"
                  >
                    Current Stock
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-4 font-semibold text-[#5F6D60]"
                  >
                    Cost Price
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-4 font-semibold text-[#5F6D60]"
                  >
                    Selling Price
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-4 font-semibold text-[#5F6D60]"
                  >
                    Expiry Date
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-4 font-semibold text-[#5F6D60]"
                  >
                    Source STK ID
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white border-[#E1E6E1]">
                {/* Render paginated filtered data */}
                {paginatedData.map((product) => (
                  <tr
                    key={product.location_id}
                    onClick={() => {
                      setDrawerLocationId(product.stock_id);
                      setShowStockSummaryDrawer(true);
                    }}
                    className="text-sm cursor-pointer hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"
                  >
                    <td className="px-4 py-4 flex font-normal text-[#5F6D60] whitespace-nowrap">
                      <p className=" py-1 px-0.5 border-[#E1E6E1] rounded-md font-semibold">
                        {product.stock_id}
                      </p>
                    </td>

                    <td className=" px-4 py-4 text-[#5F6D60] font-semibold">
                      {product.location_name}
                    </td>
                    <td className=" px-4 py-4 text-[#5F6D60] font-semibold">
                      <p
                        className={`border w-fit py-1 px-2 rounded-md font-medium ${
                          product.current_stock === 0
                            ? "border-[#B92020] text-[#B92020]"
                            : product.current_stock < 20
                            ? "border-[#D4A407] text-[#D4A407]"
                            : "border-[#2B8C34] text-[#2B8C34]"
                        }`}
                      >
                        {product.current_stock}
                      </p>
                    </td>
                    <td className=" px-4 py-4 text-[#5F6D60] font-semibold">
                      <p className="border w-fit py-1 px-2 rounded-md font-medium ">
                        ₦{product?.cost_price?.toLocaleString() || 0}
                      </p>
                    </td>
                    <td className=" px-4 py-4 text-[#5F6D60] font-semibold">
                      <p className="border w-fit py-1 px-2 rounded-md font-medium ">
                        ₦{product.selling_price?.toLocaleString() || 0}
                      </p>
                    </td>
                    <td className=" px-4 py-4 text-[#5F6D60] font-semibold">
                      {product.expiry_date ? product.expiry_date : "N/A"}
                    </td>
                    <td className=" px-4 py-4 text-[#5F6D60] font-semibold">
                      {product?.source_stock_id
                        ? product.source_stock_id
                        : "---"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {/* Pagination */}
        {filteredStockData && filteredStockData.length > 0 && (
          <div className="border-t border-[#E1E6E1]">
            <PaginationComponentOne
              totalItems={totalFilteredItems}
              page={page}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
      <StockSummaryDrawer
        showStockSummaryDrawer={showStockSummaryDrawer}
        setShowStockSummaryDrawer={setShowStockSummaryDrawer}
        locationId={drawerLocationId}
      />
    </div>
  );
};

export default InventoryTab;
